import { useRef, useState, useCallback, useEffect } from "react";

import { useLockBodyScroll } from "@narmi/design_system";

import { isSearchShortcut } from "./utils";
import { PATHS_TO_HIDE_SEARCH_BAR } from "./constants";

export const useSearchBarListeners = () => {
  const paneRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  useLockBodyScroll(isOpen);

  const toggleOpen = useCallback(() => setIsOpen((state) => !state), []);
  const setClosed = useCallback(() => setIsOpen(false), []);

  const downHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.repeat) {
        return;
      }

      if (isSearchShortcut(e)) {
        e.preventDefault();
        toggleOpen();
      } else if (e.key === "Escape") {
        setClosed();
      }
    },
    [toggleOpen, setClosed]
  );

  const clickHandler = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (paneRef.current && target && !paneRef.current.contains(target)) {
        setClosed();
      }
    },
    [setClosed]
  );

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("mousedown", clickHandler);

    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("mousedown", clickHandler);
    };
  }, [downHandler, clickHandler]);

  return { isOpen, toggleOpen, setClosed, paneRef };
};

/**
 * Hides the `<SearchBarCollapsed />` on a set list of pages.
 * The keyboard shortcut to open the expanded search remains active.
 */
export const useIsCollapsedSearchBarVisible = (): boolean => {
  const currentRoute = window.location.pathname;
  // ignore when route is empty (on dashboard)
  if (currentRoute.length <= 1) {
    return true;
  }
  return !PATHS_TO_HIDE_SEARCH_BAR.some((hiddenOnPath) =>
    currentRoute.includes(hiddenOnPath)
  );
};
